import * as React from 'react';

// API
import apibridge, { ResponseStandardFail } from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// COMPONENTS
import Button from '../../components/Button';
import OrganisationLogo from '../../components/OrganisationLogo';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/link.svg';
import Spinner from '../../assets/img/spinner.gif';

// TYPES
import { AuditReadyAudit, AccountUserUserProfile as OrganisationItemType } from '../../api/models';

const Audit: React.FC = () => {
	const user = useAppSelector((state) => state.user);

	const [activeOrganisation, setActiveOrganisation] = React.useState<OrganisationItemType>({});

	React.useEffect(() => {
		if (user.activeProfileId && user.profiles) {
			setActiveOrganisation(user.profiles?.filter((profile) => profile.id === user.activeProfileId)[0]);
		}
	}, [user]);

	// Error state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	// State to show and hide buttons
	const [showGenerateButton, setShowGenerateButton] = React.useState<boolean>(true);
	const [showLoading, setShowLoading] = React.useState<boolean>(false);
	const [showDownloadButton, setShowDownloadButton] = React.useState<boolean>(false);

	// Data for clean up function
	const timer = React.useRef<any>(null);
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	// Audit Data
	const initialAuditData: AuditReadyAudit = {
		isReady: undefined,
		url: undefined
	};

	const [auditData, setAuditData] = React.useState<AuditReadyAudit>(initialAuditData);

	const getAuditReport = async (id: string) => {
		setIsMounted(true);

		const response = await apibridge.getAuditReport(id);

		if (!response.isError && response.result) {
			const { result = initialAuditData } = response;
			// Check if both isReady is true AND url is defined
			if (result.isReady && result.url) {
				setShowLoading(false);
				setShowDownloadButton(true);
				setAuditData(result);
			} else {
				// Continue polling if isReady is true but url is not yet available, or if not ready at all
				timer.current = setTimeout(() => {
					getAuditReport(id);
				}, 3000);
			}
		} else if (response.isError) {
			setShowLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			timer.current = setTimeout(() => {
				getAuditReport(id);
			}, 3000);
		}
	};

	React.useEffect(() => {
		return () => {
			setIsMounted(true);

			clearTimeout(timer.current);
		};
	});

	const generateAudit = async () => {
		setShowGenerateButton(false);
		setShowLoading(true);

		// Getting Audit Id
		const response = await apibridge.getAuditId();

		if (!response.isError && response.result) {
			// Fetch using Audit Id - loading will be set to false when audit is ready or error occurs
			if (isMounted === true) {
				await getAuditReport(response.result);
			}
		} else {
			// If getting audit ID failed, stop loading and show error
			setShowLoading(false);

			setFormErrorMessage(
				response.validationErrors?.length
					? validationErrorsToString(response.validationErrors)
					: (response as ResponseStandardFail).message || 'An error has occurred while generating audit'
			);
		}
	};

	return (
		<div className="audit-page">
			<OrganisationLogo src={activeOrganisation.image} alt="organisation logo" />

			<h2 className="header-text">Audit of {activeOrganisation?.name}</h2>

			<p className="header-info">Generate an audit report for your organisation's child-safety compliance.</p>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<>
					{showGenerateButton && (
						<Button className="btn-primary mtop-60" onClick={generateAudit}>
							Generate Audit
						</Button>
					)}

					{showLoading && (
						<div className="waiting-status">
							<img className="spinner" src={Spinner} alt="spinner" />
							Generating audit, please wait...
						</div>
					)}

					{showDownloadButton && auditData.url && (
						<a className="btn-secondary mtop-60" href={auditData.url} download>
							<DownloadIcon className="link-icon" />
							Download your organisation's audit report
						</a>
					)}
				</>
			)}
		</div>
	);
};

export default Audit;
